import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { getApiBaseUrl } from '../utils/apiBaseUrl';

const thS = {
  padding: '10px 14px', textAlign: 'left',
  fontWeight: '700', fontSize: '12px', color: '#64748b',
  borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap',
  background: '#f8fafc',
};
const tdS = { padding: '10px 14px', color: '#334155', fontSize: '13px' };

export default function FeaturePermissionsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const apiBase = getApiBaseUrl();

  const [perms, setPerms] = useState([]);
  const [selected, setSelected] = useState(null); // { feature_key, menu_name, ... }
  const [permUsers, setPermUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  // 새 권한 생성 폼
  const [showCreate, setShowCreate] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newMenu, setNewMenu] = useState('');
  const [newPath, setNewPath] = useState('');
  const [newDesc, setNewDesc] = useState('');

  // 사용자 추가 폼
  const [addEmail, setAddEmail] = useState('');
  const [addName, setAddName] = useState('');

  const getAuthHeaders = useCallback(() => {
    const token = user?.token ? String(user.token) : '';
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [user]);

  const apiFetch = useCallback(async (path, opts = {}) => {
    const res = await fetch(`${apiBase}${path}`, {
      ...opts,
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders(), ...(opts.headers || {}) },
      credentials: 'include',
    });
    const text = await res.text();
    const json = text ? JSON.parse(text) : null;
    if (!res.ok) throw new Error(String(json?.detail || json?.message || res.status));
    return json;
  }, [apiBase, getAuthHeaders]);

  const fetchPerms = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch('/api/feature_permissions');
      if (data?.ok) setPerms(data.perms || []);
    } catch (e) {
      setMsg({ type: 'error', text: '목록 조회 실패: ' + e.message });
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  const fetchPermUsers = useCallback(async (featureKey) => {
    try {
      const data = await apiFetch(`/api/feature_permissions/${featureKey}/users`);
      if (data?.ok) setPermUsers(data.users || []);
    } catch (e) {
      setMsg({ type: 'error', text: '사용자 목록 조회 실패: ' + e.message });
    }
  }, [apiFetch]);

  useEffect(() => { fetchPerms(); }, [fetchPerms]);

  async function handleCreate(e) {
    e.preventDefault();
    if (!newKey.trim() || !newMenu.trim()) {
      setMsg({ type: 'error', text: '권한 키와 메뉴명은 필수입니다' });
      return;
    }
    try {
      await apiFetch('/api/feature_permissions', {
        method: 'POST',
        body: JSON.stringify({ feature_key: newKey, menu_name: newMenu, menu_path: newPath || null, description: newDesc || null }),
      });
      setMsg({ type: 'success', text: '기능 권한이 생성되었습니다' });
      setShowCreate(false);
      setNewKey(''); setNewMenu(''); setNewPath(''); setNewDesc('');
      fetchPerms();
    } catch (e) {
      setMsg({ type: 'error', text: '생성 실패: ' + e.message });
    }
  }

  async function handleDelete(featureKey) {
    if (!window.confirm(`'${featureKey}' 권한을 삭제하시겠습니까?\n해당 권한을 보유한 모든 사용자의 권한도 함께 삭제됩니다.`)) return;
    try {
      await apiFetch(`/api/feature_permissions/${featureKey}`, { method: 'DELETE' });
      setMsg({ type: 'success', text: '삭제되었습니다' });
      if (selected?.feature_key === featureKey) setSelected(null);
      fetchPerms();
    } catch (e) {
      setMsg({ type: 'error', text: '삭제 실패: ' + e.message });
    }
  }

  async function handleSelectPerm(perm) {
    setSelected(perm);
    setPermUsers([]);
    setAddEmail(''); setAddName('');
    await fetchPermUsers(perm.feature_key);
  }

  async function handleAddUser(e) {
    e.preventDefault();
    if (!addEmail.trim()) { setMsg({ type: 'error', text: '이메일을 입력해 주세요' }); return; }
    try {
      await apiFetch(`/api/feature_permissions/${selected.feature_key}/users`, {
        method: 'POST',
        body: JSON.stringify({ user_email: addEmail, user_name: addName || null }),
      });
      setMsg({ type: 'success', text: `${addEmail} 에게 권한이 부여되었습니다` });
      setAddEmail(''); setAddName('');
      fetchPermUsers(selected.feature_key);
      fetchPerms();
    } catch (e) {
      setMsg({ type: 'error', text: '추가 실패: ' + e.message });
    }
  }

  async function handleRemoveUser(userEmail) {
    if (!window.confirm(`${userEmail} 의 권한을 회수하시겠습니까?`)) return;
    try {
      await apiFetch(`/api/feature_permissions/${selected.feature_key}/users/${encodeURIComponent(userEmail)}`, { method: 'DELETE' });
      setMsg({ type: 'success', text: '권한이 회수되었습니다' });
      fetchPermUsers(selected.feature_key);
      fetchPerms();
    } catch (e) {
      setMsg({ type: 'error', text: '회수 실패: ' + e.message });
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f0f4f8' }}>
      <Header leftActions={
        <button
          type="button"
          onClick={() => navigate('/')}
          style={{
            height: '34px', padding: '0 12px',
            background: 'rgba(255,255,255,0.15)',
            color: 'white',
            border: '1px solid rgba(255,255,255,0.40)',
            borderRadius: '10px',
            cursor: 'pointer',
            fontWeight: 900,
            fontSize: '13px',
            whiteSpace: 'nowrap',
          }}
        >
          ☰ 전체
        </button>
      } />
      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        @keyframes modalIn { from{opacity:0;transform:scale(0.95)} to{opacity:1;transform:scale(1)} }
        .fp-perm-row { cursor:pointer; transition:background 0.12s; }
        .fp-perm-row:hover { background:#f0f9ff !important; }
        .fp-perm-row.selected { background:#eff6ff !important; border-left:3px solid #2563eb !important; }
      `}</style>

      <div style={{ padding: '20px 16px 40px', maxWidth: '860px', margin: '0 auto' }}>

        {/* 헤더 배너 */}
        <div style={{
          background: 'linear-gradient(135deg,#1e3a8a,#2563eb)',
          borderRadius: '20px', padding: '24px 20px', color: '#fff',
          marginBottom: '20px',
          boxShadow: '0 8px 32px rgba(37,99,235,0.28)',
        }}>
          <div style={{ fontSize: '22px', fontWeight: '900', letterSpacing: '-0.02em' }}>🔐 기능 권한 관리</div>
          <div style={{ fontSize: '13px', opacity: 0.75, marginTop: '4px' }}>
            메뉴별 세부 기능 권한을 정의하고 사용자에게 부여합니다
          </div>
        </div>

        {/* 알림 */}
        {msg && (
          <div style={{
            padding: '11px 16px', borderRadius: '12px', marginBottom: '16px',
            fontSize: '13px', fontWeight: '600', animation: 'fadeUp 0.2s ease',
            background: msg.type === 'success' ? '#d1fae5' : '#fce7f3',
            color: msg.type === 'success' ? '#065f46' : '#9d174d',
            border: `1px solid ${msg.type === 'success' ? '#a7f3d0' : '#fbcfe8'}`,
          }}>
            {msg.type === 'success' ? '✓ ' : '✕ '}{msg.text}
          </div>
        )}

        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', flexWrap: 'wrap' }}>

          {/* 좌: 권한 목록 */}
          <div style={{ flex: '1 1 280px', minWidth: 0 }}>
            <div style={{
              background: '#fff', borderRadius: '20px',
              boxShadow: '0 2px 12px rgba(15,23,42,0.07)', overflow: 'hidden',
            }}>
              <div style={{
                padding: '16px 20px', borderBottom: '1px solid #f1f5f9',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <span style={{ fontWeight: '800', fontSize: '14px', color: '#0f172a' }}>기능 권한 목록</span>
                <button type="button" onClick={() => { setShowCreate(v => !v); setMsg(null); }}
                  style={{
                    padding: '5px 12px', borderRadius: '99px', border: 'none',
                    background: showCreate ? '#f1f5f9' : '#2563eb',
                    color: showCreate ? '#64748b' : '#fff',
                    fontWeight: '700', fontSize: '12px', cursor: 'pointer',
                  }}>
                  {showCreate ? '취소' : '+ 추가'}
                </button>
              </div>

              {/* 새 권한 생성 폼 */}
              {showCreate && (
                <form onSubmit={handleCreate} style={{
                  padding: '16px 20px', borderBottom: '1px solid #f1f5f9',
                  background: '#f8fafc', animation: 'fadeUp 0.18s ease',
                }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', marginBottom: '10px' }}>새 기능 권한</div>
                  {[
                    { label: '권한 키 *', val: newKey, set: setNewKey, ph: 'attendance_manager' },
                    { label: '메뉴명 *', val: newMenu, set: setNewMenu, ph: '출퇴근 체크' },
                    { label: '라우트 경로', val: newPath, set: setNewPath, ph: '/attendance' },
                    { label: '설명', val: newDesc, set: setNewDesc, ph: '출퇴근 기록 관리 권한' },
                  ].map(({ label, val, set, ph }) => (
                    <div key={label} style={{ marginBottom: '8px' }}>
                      <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '3px' }}>{label}</div>
                      <input value={val} onChange={e => set(e.target.value)} placeholder={ph}
                        style={{
                          width: '100%', height: '32px', padding: '0 10px', borderRadius: '8px',
                          border: '1px solid #e2e8f0', fontSize: '12px', color: '#334155',
                          boxSizing: 'border-box', outline: 'none',
                        }} />
                    </div>
                  ))}
                  <button type="submit"
                    style={{
                      width: '100%', padding: '9px 0', borderRadius: '10px', border: 'none',
                      background: 'linear-gradient(135deg,#1e3a8a,#2563eb)',
                      color: '#fff', fontWeight: '700', fontSize: '13px', cursor: 'pointer', marginTop: '4px',
                    }}>저장</button>
                </form>
              )}

              {/* 목록 */}
              {loading ? (
                <div style={{ padding: '30px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>불러오는 중...</div>
              ) : perms.length === 0 ? (
                <div style={{ padding: '30px', textAlign: 'center', color: '#cbd5e1', fontSize: '13px' }}>등록된 권한이 없습니다</div>
              ) : perms.map((p) => (
                <div key={p.feature_key}
                  className={`fp-perm-row${selected?.feature_key === p.feature_key ? ' selected' : ''}`}
                  onClick={() => handleSelectPerm(p)}
                  style={{
                    padding: '14px 20px', borderBottom: '1px solid #f1f5f9',
                    borderLeft: selected?.feature_key === p.feature_key ? '3px solid #2563eb' : '3px solid transparent',
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontWeight: '700', fontSize: '13px', color: '#0f172a' }}>{p.menu_name}</div>
                      <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                        <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: '4px' }}>{p.feature_key}</code>
                        {p.menu_path && <span style={{ marginLeft: '6px' }}>{p.menu_path}</span>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{
                        padding: '2px 9px', borderRadius: '99px', fontSize: '11px', fontWeight: '700',
                        background: '#eff6ff', color: '#1e40af',
                      }}>{p.user_count}명</span>
                      <button type="button"
                        onClick={e => { e.stopPropagation(); handleDelete(p.feature_key); }}
                        style={{
                          width: '22px', height: '22px', borderRadius: '6px',
                          border: '1px solid #fecaca', background: '#fff',
                          color: '#ef4444', fontSize: '12px', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>✕</button>
                    </div>
                  </div>
                  {p.description && (
                    <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>{p.description}</div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* 우: 선택된 권한의 사용자 목록 */}
          <div style={{ flex: '1 1 320px', minWidth: 0 }}>
            {selected ? (
              <div style={{
                background: '#fff', borderRadius: '20px',
                boxShadow: '0 2px 12px rgba(15,23,42,0.07)', overflow: 'hidden',
                animation: 'fadeUp 0.2s ease',
              }}>
                <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ fontWeight: '800', fontSize: '14px', color: '#0f172a' }}>
                    {selected.menu_name} 권한자
                  </div>
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                    <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: '4px' }}>{selected.feature_key}</code>
                  </div>
                </div>

                {/* 사용자 추가 폼 */}
                <form onSubmit={handleAddUser} style={{
                  padding: '14px 20px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc',
                }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', marginBottom: '8px' }}>권한 부여</div>
                  <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                    <input value={addEmail} onChange={e => setAddEmail(e.target.value)}
                      placeholder="이메일 *" type="email"
                      style={{
                        flex: 2, height: '32px', padding: '0 10px', borderRadius: '8px',
                        border: '1px solid #e2e8f0', fontSize: '12px', color: '#334155', outline: 'none',
                      }} />
                    <input value={addName} onChange={e => setAddName(e.target.value)}
                      placeholder="이름"
                      style={{
                        flex: 1, height: '32px', padding: '0 10px', borderRadius: '8px',
                        border: '1px solid #e2e8f0', fontSize: '12px', color: '#334155', outline: 'none',
                      }} />
                  </div>
                  <button type="submit"
                    style={{
                      width: '100%', padding: '8px 0', borderRadius: '10px', border: 'none',
                      background: 'linear-gradient(135deg,#064e3b,#047857)',
                      color: '#fff', fontWeight: '700', fontSize: '12px', cursor: 'pointer',
                    }}>추가</button>
                </form>

                {/* 사용자 테이블 */}
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={thS}>이름</th>
                        <th style={thS}>이메일</th>
                        <th style={thS}>부여자</th>
                        <th style={{ ...thS, textAlign: 'center', width: '50px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {permUsers.length === 0 ? (
                        <tr>
                          <td colSpan={4} style={{ padding: '30px', textAlign: 'center', color: '#cbd5e1', fontSize: '13px' }}>
                            등록된 사용자가 없습니다
                          </td>
                        </tr>
                      ) : permUsers.map((u) => (
                        <tr key={u.seq} style={{ borderTop: '1px solid #f1f5f9' }}>
                          <td style={{ ...tdS, fontWeight: '600', color: '#0f172a' }}>{u.user_name || '-'}</td>
                          <td style={{ ...tdS, fontSize: '12px', color: '#64748b' }}>{u.user_email}</td>
                          <td style={{ ...tdS, fontSize: '12px', color: '#94a3b8' }}>{u.granted_by || '-'}</td>
                          <td style={{ ...tdS, textAlign: 'center' }}>
                            <button type="button"
                              onClick={() => handleRemoveUser(u.user_email)}
                              style={{
                                padding: '3px 8px', borderRadius: '6px',
                                border: '1px solid #fecaca', background: '#fff',
                                color: '#ef4444', fontSize: '11px', fontWeight: '700', cursor: 'pointer',
                              }}>회수</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div style={{
                background: '#fff', borderRadius: '20px',
                boxShadow: '0 2px 12px rgba(15,23,42,0.07)',
                padding: '40px 20px', textAlign: 'center', color: '#94a3b8',
              }}>
                <div style={{ fontSize: '32px', marginBottom: '10px' }}>👈</div>
                <div style={{ fontSize: '13px', fontWeight: '600' }}>좌측에서 권한을 선택하면<br />권한자 목록을 관리할 수 있습니다</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
